/**
 * Provides access to the funding completion callback for a given flow.
 *
 * Call `invokeCallback` from a success screen to fire the stored callback
 * and automatically clear it from the atom so it cannot be invoked twice.
 *
 * Returns `hasCallback: true` when the current navigation was initiated from
 * a context that expects a completion callback (e.g. the Progress Tracker
 * funding screen). Use this in place of checking the `event` URL param.
 *
 * Unmount cleanup:
 *  - If the user backs out or abandons the flow before reaching the success
 *    screen, the component unmounts with the callback still in the atom.
 *  - On unmount, the callback is fired with { success: false } and cleared.
 *    This ensures callers are always notified, even for abandoned flows.
 */

import { FundingOption } from '@varo/graphql-schema-types';
import {
  FundingCallback,
  fundingCallbackAtom,
  FundingOutcome,
} from 'features/onboarding/state/fundingCallbackAtom';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

export function useFundingCallback(flow: FundingOption): {
  hasCallback: boolean;
  invokeCallback: (outcome: FundingOutcome) => void;
} {
  const [callbackMap, setCallbackMap] = useAtom(fundingCallbackAtom);
  const storedCallback: FundingCallback | undefined = callbackMap[flow];

  const clearEntry = useCallback(() => {
    setCallbackMap((currentMap) => {
      const updatedMap = { ...currentMap };
      delete updatedMap[flow];
      return updatedMap;
    });
  }, [flow, setCallbackMap]);

  const invokeCallback = useCallback(
    (outcome: FundingOutcome) => {
      if (storedCallback) {
        storedCallback(outcome);
        clearEntry();
      }
    },
    [storedCallback, clearEntry],
  );

  useEffect(() => {
    return () => {
      setCallbackMap((currentMap) => {
        const callback = currentMap[flow];
        if (callback) {
          callback({ success: false });
          const updatedMap = { ...currentMap };
          delete updatedMap[flow];
          return updatedMap;
        }
        return currentMap;
      });
    };
  }, [flow, setCallbackMap]);

  return { hasCallback: Boolean(storedCallback), invokeCallback };
}
